import { Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ButtonTemplateOverride, PhoneButtonTemplateInventory } from '../../../shared/models/generated/ziroModels';
import { combineLatest, Subscription } from 'rxjs';
import { BottomNavButton } from '../../../shared/bottom-nav/bottom-nav.component';
import {
  BottomNavService,
  BottomNavUpdateButtonsList,
  BottomNavUpdateButtonState,
  BottomNavUpdateState,
} from '../../../shared/bottom-nav/bottom-nav.service';
import { ToastTypes } from '../../../shared/services/abstract/toast.service.abstract';
import { DefaultPhoneButtonTemplateResource } from '../../resources/default-phone-button-template.resource';
import { ToastService } from '../../../shared/services/toast.service';
import { ButtonStyles } from '../../../button/button.component';
import { ZiroIcons } from '../../../shared/models/ziro-icons.enum';
import { BreadcrumbsService } from '../../../shared/breadcrumbs/breadcrumbs.service';
import { ServersContext } from '../../contexts/servers.context';
import { FilterSelectOption } from '../../../shared/tables/datatable/filter-select/filter-select.component';
import {
  DatatableColumn,
  DatatableOnRowFieldChange,
  DatatableRow,
} from '../../../shared/tables/datatable/datatable.models';
import { DatatableComponent } from '../../../shared/tables/datatable/datatable.component';
import { DatatableFieldTypes } from '../../../shared/tables/datatable/datatable-field.models';
import { ZiroFormsValidationState } from '../../../forms/ziro-forms-models';

interface DefaultPhoneButtonTemplateRow extends DatatableRow {
  id: string;
  model: string;
  protocol: string;
  cucmServer: string;
  defaultTemplate: string;
}

@Component({
  selector: 'ziro-admin-phone-button-templates',
  templateUrl: './default-phone-button-templates.component.html',
  styleUrls: ['../../admin-page.scss'],
  standalone: false,
})
export class DefaultPhoneButtonTemplatesComponent implements OnInit, OnDestroy {
  private defaultPhoneButtonTemplateResource = inject(DefaultPhoneButtonTemplateResource);
  private bottomNavService = inject(BottomNavService);
  private toastService = inject(ToastService);
  private serversContext = inject(ServersContext);
  private breadcrumbsService = inject(BreadcrumbsService);

  @ViewChild(DatatableComponent) private _datatable: DatatableComponent<DefaultPhoneButtonTemplateRow>;

  isLoading = true;
  isSaving = false;

  columns: DatatableColumn<DefaultPhoneButtonTemplateRow>[] = [
    { name: 'model', label: 'tkey;admin.phone_button_templates.header.model_name', columnSize: 'col-sm-5' },
    { name: 'protocol', label: 'tkey;admin.phone_button_templates.header.protocol', columnSize: 'col-sm-2' },
    { name: 'cucmServer', label: 'tkey;admin.phone_button_templates.header.cucm_server', columnSize: 'col-sm-2' },
    {
      name: 'defaultTemplate',
      label: 'tkey;admin.phone_button_templates.header.default_template',
      columnSize: 'col-sm-5',
      columnCharacterWidth: 40,
    },
  ];

  rows: DefaultPhoneButtonTemplateRow[] = [];

  @ViewChild(DatatableComponent) datatable: DatatableComponent<DefaultPhoneButtonTemplateRow>;

  private _overrides: ButtonTemplateOverride[];
  private _serverIdToNameMap: Map<number, string>;
  private _isSubmitted = false;
  private _templateInventory: PhoneButtonTemplateInventory[] = [];
  private _subscription = new Subscription();

  bottomNavButtons = [
    {
      id: 'saveDefaultTemplateConfig',
      label: 'tkey;admin.phone_button_templates.bottom_nav.save.label',
      dataAutomation: 'admin-phone-button-templates-save-button',
      buttonClass: ButtonStyles.PRIMARY,
      state: {
        pending: false,
        buttonDisableState: { disabled: false, tooltipKey: '' },
      },
      icon: ZiroIcons.SAVE_SUCCESS,
      cb: () => {
        this._saveDefaultTemplateConfig();
      },
    },
  ] as BottomNavButton[];

  uniqueModels: string[] = [];
  uniqueProtocols: string[] = [];
  uniqueServers: string[] = [];

  ngOnInit(): void {
    this.breadcrumbsService.updateBreadcrumbs([{ label: 'tkey;admin.phone_button_templates.title' }]);

    const sub = combineLatest([
      this.serversContext.state$,
      this.defaultPhoneButtonTemplateResource.getPhoneButtonTemplateInventory(),
      this.defaultPhoneButtonTemplateResource.getDefaultPhoneButtonTemplateOverrides(),
    ]).subscribe(([servers, templateInventory, templateOverrides]) => {
      this._templateInventory = templateInventory;
      this._serverIdToNameMap = servers.reduce<Map<number, string>>(
        (map, server) => map.set(server.id, server.description),
        new Map<number, string>(),
      );
      this.uniqueModels = Array.from(new Set(templateInventory.map((template) => template.model)));
      this.uniqueProtocols = Array.from(new Set(templateInventory.map((template) => template.protocol)));
      this.uniqueServers = Array.from(new Set(templateInventory.map((template) => template.cucmId))).map((serverId) =>
        this._serverIdToNameMap.get(serverId),
      );
      this._buildRows(templateInventory, templateOverrides);
      this._overrides = templateOverrides;
      this.bottomNavService.dispatch(new BottomNavUpdateButtonsList(this.bottomNavButtons));
      this.isLoading = false;
    });
    this._subscription.add(sub);
  }

  ngOnDestroy(): void {
    this.breadcrumbsService.clearBreadcrumbs();
    this._subscription.unsubscribe();
  }

  isModelMatch(filters: string[], row: DefaultPhoneButtonTemplateRow): boolean {
    return !filters.length || filters.includes(row.model);
  }

  isProtocolMatch(filters: string[], row: DefaultPhoneButtonTemplateRow): boolean {
    return !filters.length || filters.includes(row.protocol);
  }

  isCucmServerMatch(filters: string[], row: DefaultPhoneButtonTemplateRow): boolean {
    return !filters.length || filters.includes(row.cucmServer);
  }

  private _buildRows(
    templateInventory: PhoneButtonTemplateInventory[],
    templateOverrides: ButtonTemplateOverride[],
  ): void {
    // The template inventory is a list of templates. It isn't very UI-friendly.
    // What we have to do here is group up the templates by model, protocol and cucmId.
    // We should display one row per unique model-protocol-server.
    // The options for the select on that row will be all the templates with that particular model-protocol-server.
    // There might also be an override set which no longer exists on cucm. So be sure to account for that.
    this.rows = templateInventory.reduce<DefaultPhoneButtonTemplateRow[]>((array, template) => {
      const cucmServer = this._serverIdToNameMap.get(template.cucmId);

      const modelProtocolServer = { model: template.model, protocol: template.protocol, cucmServer };

      if (
        !array.some(
          (row) =>
            row.model === modelProtocolServer.model &&
            row.protocol === modelProtocolServer.protocol &&
            row.cucmServer === modelProtocolServer.cucmServer,
        )
      ) {
        const existingOverride = templateOverrides.find(
          (override) =>
            override.model === template.model &&
            override.protocol === template.protocol &&
            override.cucmId === template.cucmId,
        );

        const possibleTemplates = templateInventory.filter((templateInv) => {
          return (
            templateInv.model === template.model &&
            templateInv.protocol === template.protocol &&
            templateInv.cucmId === template.cucmId
          );
        });

        const options: FilterSelectOption[] = possibleTemplates.map((templateInv) => {
          return {
            label: templateInv.name,
            value: templateInv,
          };
        });

        let hasInitialError = false;
        if (existingOverride && options.every((option) => existingOverride.templateId !== option.value.id)) {
          // if the template has somehow been deleted from cucm, we need to show it in the options
          // and display an error on the row.
          options.push({
            label: existingOverride.name,
            value: {
              id: existingOverride.templateId,
              name: existingOverride.name,
              model: existingOverride.model,
              protocol: existingOverride.protocol,
              cucmId: existingOverride.cucmId,
              phoneRefs: 0,
              cucmDeviceDefault: false,
              standardTemplate: false,
            },
          });
          hasInitialError = true;
        }

        array.push({
          ...modelProtocolServer,
          id: `${template.model}-${template.protocol}-${template.cucmId}`,
          defaultTemplate: existingOverride ? existingOverride.name : '',
          fields: {
            defaultTemplate: {
              type: DatatableFieldTypes.SELECT,
              options,
              validation: [
                {
                  validator: (value: FilterSelectOption | string): ZiroFormsValidationState => {
                    const templateId = typeof value === 'string' ? existingOverride?.templateId : value?.value?.id;
                    return !(hasInitialError && templateId === existingOverride?.templateId)
                      ? ZiroFormsValidationState.VALID
                      : ZiroFormsValidationState.INVALID;
                  },
                  message: () => 'tkey;admin.phone_button_templates.feedback.template_invalid.text',
                },
              ],
              value: existingOverride?.name,
            },
          },
          cssClass: hasInitialError ? 'table-danger' : '',
        });
      }
      return array;
    }, []);
  }

  private _setSaveButtonPendingState(newState: boolean): void {
    this.bottomNavService.dispatch(
      new BottomNavUpdateButtonState({
        id: 'saveDefaultTemplateConfig',
        state: {
          pending: newState,
          buttonDisableState: { disabled: newState, tooltipKey: '' },
        },
      }),
    );
  }

  private _scrollToFirstError(): void {
    const firstErrorRow = document.querySelector('.table-danger');
    if (firstErrorRow) {
      firstErrorRow.scrollIntoView();
    }
  }

  private _saveDefaultTemplateConfig(): void {
    this._isSubmitted = true;
    const hasErrors = this.rows.some((row) => this.datatable.isErrorPresentOnRow(row));
    if (hasErrors) {
      this.bottomNavService.dispatch(
        new BottomNavUpdateState({
          hasValidationError: true,
        }),
      );

      this._scrollToFirstError();
      return;
    }

    this.isSaving = true;
    this._setSaveButtonPendingState(true);

    this.defaultPhoneButtonTemplateResource.saveDefaultPhoneButtonTemplateOverrides(this._overrides).subscribe(() => {
      this.toastService.push(
        ToastTypes.SUCCESS,
        ZiroIcons.TABLE,
        'tkey;shared.toast.save.success.title',
        'tkey;admin.phone_button_templates.toast.saved',
      );

      this.isSaving = false;
      this._setSaveButtonPendingState(false);
    });
  }

  onTemplateChange(event: DatatableOnRowFieldChange<any>): void {
    if (this._isSubmitted) {
      const hasError = this.rows.some((row) => this.datatable.isErrorPresentOnRow(row));
      this.bottomNavService.dispatch(
        new BottomNavUpdateState({
          hasValidationError: hasError,
        }),
      );
    }

    const newValue = (event.newValue as FilterSelectOption).value as PhoneButtonTemplateInventory;

    const newOverride: ButtonTemplateOverride = {
      templateId: newValue.id,
      name: newValue.name,
      model: newValue.model,
      protocol: newValue.protocol,
      cucmId: newValue.cucmId,
    };
    const existingOverride = this._overrides.findIndex((override) => {
      return (
        newValue.model === override.model &&
        newValue.protocol === override.protocol &&
        newValue.cucmId === override.cucmId
      );
    });
    if (existingOverride > -1) {
      this._overrides.splice(existingOverride, 1, newOverride);
    } else {
      this._overrides.push(newOverride);
    }

    const rowId = `${newValue.model}-${newValue.protocol}-${newValue.cucmId}`;
    const row = this.rows.find((r) => r.id === rowId);
    if (!row) {
      return;
    }

    row.defaultTemplate = newValue.name;

    if (row.fields?.defaultTemplate) {
      row.fields.defaultTemplate.value = newValue.name;
    }

    const templateExistsInInventory = this._templateInventory.some(
      (t) =>
        t.model === newValue.model &&
        t.protocol === newValue.protocol &&
        t.cucmId === newValue.cucmId &&
        t.id === newValue.id,
    );
    row.cssClass = templateExistsInInventory ? '' : 'table-danger';
  }
}
